<script setup lang="ts">
// Docked summary bar (sticky top strip + Documents) for the single-scheme journey; also mounted on the Additional Information step.
import { ref } from 'vue';
import { useSingleSchemeSummary } from './useSingleSchemeSummary';
import PdfIcon from '@/components/Common/PdfIcon.vue';

defineProps<{ showUndock?: boolean; loading?: boolean }>();
defineEmits<{ (e: 'undock'): void }>();

const { scheme, price, total, monthly, quoteRef, documents, docName } = useSingleSchemeSummary();
const docsOpen = ref(false);
// Collapse hides the bar off-screen (leaving the pull-tab) so the app search bar behind it is reachable.
const collapsed = ref(false);
const toggleCollapse = () => { collapsed.value = !collapsed.value; if (collapsed.value) docsOpen.value = false; };
</script>

<template>
    <div class="single-scheme-quote single-scheme-quote-standalone" v-if="scheme">
        <div class="ssq-dockbar" :class="{ collapsed }">
            <div class="db-ref">Quotation <b>{{ quoteRef }}</b></div>
            <div class="db-pay">
                <transition name="ssq-fade" mode="out-in">
                    <span v-if="loading" class="db-loading"><span class="ssq-spin sm"></span></span>
                    <span v-else class="db-pay-vals">
                        <span><small>Annually</small> <b>{{ price(total) }}</b></span>
                        <span v-if="monthly"><small>Monthly</small> <b>{{ price(monthly.amount) }}</b>/mo</span>
                    </span>
                </transition>
            </div>
            <div class="db-actions">
                <button v-if="documents.length" type="button" class="db-docs-btn" :class="{ on: docsOpen }" @click="docsOpen = !docsOpen">
                    <em class="far fa-file-lines"></em> Documents
                    <em class="far chev fa-chevron-down"></em>
                </button>
                <span class="db-total">
                    <transition name="ssq-fade" mode="out-in">
                        <span v-if="loading" class="ssq-spin sm"></span>
                        <span v-else>{{ price(total) }}</span>
                    </transition>
                </span>
                <button v-if="showUndock" type="button" class="icon-btn" title="Undock summary" @click="$emit('undock')">
                    <em class="far fa-arrow-down"></em>
                </button>
            </div>

            <!-- Pull-tab flows after the documents panel so it moves down instead of overlapping when docs are open. -->
            <div class="db-below">
                <div v-if="docsOpen && documents.length" class="db-docs-panel">
                    <ul class="doclist doclist-wrap">
                        <li v-for="(doc, i) in documents" :key="i"><PdfIcon /><a :href="doc.link" target="_blank" rel="noopener">{{ docName(doc.name) }}</a></li>
                    </ul>
                </div>
                <button type="button" class="db-collapse" :title="collapsed ? 'Show summary' : 'Hide summary'" @click="toggleCollapse">
                    <em class="far" :class="collapsed ? 'fa-chevron-down' : 'fa-chevron-up'"></em>
                </button>
            </div>
        </div>
    </div>
</template>
